/**
 * Phase 18.7 — Shared PDF download UX.
 *
 * Provides one consistent loading/feedback flow for every PDF download
 * across the app:
 *   - `loading` flag flips immediately on click so the button disables
 *     (prevents double-clicks → no duplicate invoice numbers, no duplicate
 *     document_copy emails).
 *   - `isSlow` flag flips after 3 s so the UI can show a calm extra hint
 *     ("Dit kan enkele seconden duren bij grotere documenten.").
 *   - Toast on success ("PDF is gegenereerd en wordt gedownload.") and
 *     on failure ("PDF kon niet worden gegenereerd. Probeer het opnieuw.").
 *   - Triggers the actual browser download and revokes the object URL.
 *
 * Caller passes a `downloader` async function that resolves to a Blob
 * (or to an object `{ blob, filename }`). Most call sites already have
 * a `responseType: "blob"` axios call ready — they just pass the
 * response data to this hook.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { useGlobalLoading } from "../context/GlobalLoadingProvider";

const DEFAULT_MSG = {
  nl: {
    success: "PDF is gegenereerd en wordt gedownload.",
    error: "PDF kon niet worden gegenereerd. Probeer het opnieuw.",
    slow: "Dit kan enkele seconden duren bij grotere documenten.",
  },
  en: {
    success: "PDF generated and downloading.",
    error: "PDF could not be generated. Please try again.",
    slow: "This may take a few seconds for larger documents.",
  },
};

export default function useDownloadPdf({ lang = "nl", slowMs = 3000 } = {}) {
  const [loading, setLoading] = useState(false);
  const [isSlow, setIsSlow] = useState(false);
  const slowTimer = useRef(null);
  const inflight = useRef(false); // hard guard against double-submits
  const globalLoading = useGlobalLoading();

  useEffect(() => () => {
    if (slowTimer.current) clearTimeout(slowTimer.current);
  }, []);

  const msgs = DEFAULT_MSG[lang] || DEFAULT_MSG.nl;

  /**
   * Run the downloader and wire all the UX feedback.
   *
   * @param {Function} downloader   async () => Blob | { blob, filename, headers? }
   * @param {Object}   opts
   * @param {string}  [opts.filename]        — fallback when downloader doesn't return one
   * @param {string}  [opts.successMessage]  — override toast text
   * @param {string}  [opts.errorMessage]    — override toast text
   * @param {string}  [opts.loadingMessage]  — contextuele overlay-message
   *                                           (bv "Warehouse Receipt wordt
   *                                           gegenereerd…"). Iteratie A —
   *                                           universeel loading systeem.
   * @param {string}  [opts.taskId]          — dedup-key voor global overlay
   * @param {Function}[opts.onError]         — receives the raw error so the
   *                                            caller can also surface inline.
   * @param {Function}[opts.onSuccess]       — receives `{ filename, headers? }`
   * @param {Function}[opts.parseErrorMessage] — receives the raw error and
   *                                              returns a user-friendly
   *                                              message (e.g. parse blob JSON).
   * @returns Promise<{ ok: true, filename } | { ok: false, error }>
   */
  const download = useCallback(async (downloader, opts = {}) => {
    if (inflight.current) {
      // Hard de-dupe: caller doesn't have to remember to disable the button.
      return { ok: false, error: new Error("duplicate_request_ignored") };
    }
    inflight.current = true;
    setLoading(true);
    setIsSlow(false);
    if (slowTimer.current) clearTimeout(slowTimer.current);
    slowTimer.current = setTimeout(() => setIsSlow(true), slowMs);

    // Iteratie A — track in global loading overlay als opts.loadingMessage
    // aanwezig. runTask verzorgt de 300ms delay + success/error overlay.
    const useGlobalOverlay = !!opts.loadingMessage;
    const taskId = opts.taskId || `pdf_${Date.now()}`;

    const doWork = async () => {
      const result = await downloader();
      const blob = result instanceof Blob ? result : result?.blob;
      if (!(blob instanceof Blob)) {
        throw new Error("Downloader did not return a Blob");
      }
      const filename =
        (typeof result === "object" && result?.filename) ||
        opts.filename ||
        "document.pdf";
      _triggerBrowserDownload(blob, filename);
      if (opts.onSuccess) opts.onSuccess({ filename, headers: result?.headers });
      return { ok: true, filename };
    };

    try {
      let res;
      if (useGlobalOverlay) {
        res = await globalLoading.runTask({
          id: taskId,
          message: opts.loadingMessage,
          successMessage: opts.successMessage || msgs.success,
          run: doWork,
        });
      } else {
        res = await doWork();
        toast.success(opts.successMessage || msgs.success);
      }
      return res;
    } catch (err) {
      let msg = opts.errorMessage || msgs.error;
      if (opts.parseErrorMessage) {
        try {
          const parsed = await opts.parseErrorMessage(err);
          if (parsed) msg = parsed;
        } catch { /* fall back to default */ }
      }
      if (!useGlobalOverlay) {
        toast.error(msg);
      }
      if (opts.onError) opts.onError(err);
      return { ok: false, error: err };
    } finally {
      if (slowTimer.current) clearTimeout(slowTimer.current);
      setIsSlow(false);
      setLoading(false);
      inflight.current = false;
    }
  }, [msgs.success, msgs.error, slowMs, globalLoading]);

  return {
    download,
    loading,
    isSlow,
    slowHint: msgs.slow,
  };
}

function _triggerBrowserDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
